/* eslint-disable @typescript-eslint/no-explicit-any */

import { NO_RESTRICTIONS } from 'components/EmailPolicyChoice';
import { RefundTypes } from 'constants/RefundTypes';
import { core, toJSON } from '@tock/coretypes';
import AppConstants from 'constants/AppConstants';

import { Reducer } from 'redux';
import { cloneDeep } from 'es-toolkit';
import protos from 'utils/protos';

const { Discount } = protos;

export type CancellationType = 'NORMAL' | 'FLEX';

interface PurchaseState {
  isCancelling: boolean;
  isRefunding: boolean;
  isComping: boolean;
  isTransferring: boolean;
  requestInProgress: boolean;
  cancellationRequest: {
    cancellationType: CancellationType;
    purchaseDetail: core.PastPurchase | Record<PropertyKey, never>;
  };
  refundRequest: {
    purchaseDetail: core.PastPurchase;
    initialPurchaseOrder?: core.PurchaseOrder;
    newPurchaseOrder?: core.PurchaseOrder;
    refund: {
      amountCents: number;
      refundType: RefundTypes | null;
    };
    emailPolicy: {
      type: core.EmailPolicy.Type;
    };
    discountType: core.Discount.DiscountType;
  };
  refundTypes: SelectOption[];
  purchaseDetailForTransfer: any;
}

const refundTypes = [
  {
    key: RefundTypes.NONE,
    value: RefundTypes.NONE,
    label: 'No refund',
  },
  {
    key: RefundTypes.FULL,
    value: RefundTypes.FULL,
    label: 'Full refund',
  },
  {
    key: RefundTypes.PARTIAL,
    value: RefundTypes.PARTIAL,
    label: 'Partial refund',
  },
];

const noPartialRefundTypes = [
  {
    key: RefundTypes.NONE,
    value: RefundTypes.NONE,
    label: 'No refund',
  },
  {
    key: RefundTypes.FULL,
    value: RefundTypes.FULL,
    label: 'Full refund',
  },
];

export const initialState: PurchaseState = {
  isCancelling: false,
  isRefunding: false,
  isComping: false,
  isTransferring: false,
  requestInProgress: false,
  cancellationRequest: {
    purchaseDetail: {},
    cancellationType: 'NORMAL',
  },
  refundRequest: {
    purchaseDetail: {} as any,
    initialPurchaseOrder: undefined,
    newPurchaseOrder: undefined,
    refund: {
      amountCents: 0,
      refundType: null,
    },
    // Default to COMP.
    discountType: Discount.DiscountType.SUBTOTAL_COMP,
    emailPolicy: NO_RESTRICTIONS,
  },
  refundTypes,
  purchaseDetailForTransfer: {},
};

const reducer: Reducer<PurchaseState, BaseTockAction> = (
  state: PurchaseState = initialState,
  action: BaseTockAction
) => {
  const { type, payload, context } = action;
  switch (type) {
    case AppConstants.PURCHASE_CANCEL_OPEN: {
      return {
        ...initialState,
        refundTypes:
          payload.purchaseDetail?.ticketType?.deliveryServiceProvider === 'INTERNAL'
            ? cloneDeep(refundTypes)
            : cloneDeep(noPartialRefundTypes),
        cancellationRequest: {
          ...initialState.cancellationRequest,
          purchaseDetail: payload.purchaseDetail,
          cancellationType:
            payload.cancellationType || initialState.cancellationRequest.cancellationType,
        },
        isCancelling: true,
      };
    }
    case AppConstants.PURCHASE_COMP_OPEN_DONE: {
      return {
        ...initialState,
        refundTypes: cloneDeep(refundTypes),
        refundRequest: {
          ...initialState.refundRequest,
          purchaseDetail: context,
          newPurchaseOrder: toJSON(payload),
        },
        isComping: true,
      };
    }
    case AppConstants.PURCHASE_TRANSFER_OPEN: {
      return {
        ...initialState,
        isTransferring: true,
        purchaseDetailForTransfer: payload,
      };
    }

    case AppConstants.PURCHASE_REFUND_UPDATE: {
      return {
        ...state,
        refundRequest: {
          ...state.refundRequest,
          ...payload,
          discountType: payload.refund.discountType,
        },
      };
    }
    case AppConstants.PURCHASE_REFUND_UPDATE_PURCHASE_ORDER_DONE: {
      return {
        ...state,
        refundRequest: {
          ...state.refundRequest,
          newPurchaseOrder: toJSON(payload),
        },
      };
    }
    case AppConstants.PURCHASE_CANCEL_DONE:
    case AppConstants.PURCHASE_REFUND_DONE:
    case AppConstants.PURCHASE_TRANSFER_DONE:
    case AppConstants.PURCHASE_CHARGE_BOOKING_POLICY_FEE_DONE:
    case AppConstants.PURCHASE_REFUND_BOOKING_POLICY_FEE_DONE: {
      return {
        ...state,
        requestInProgress: false,
      };
    }
    case AppConstants.PURCHASE_CANCEL_STARTING:
    case AppConstants.PURCHASE_REFUND_STARTING:
    case AppConstants.PURCHASE_TRANSFER_STARTING:
    case AppConstants.PURCHASE_CHARGE_BOOKING_POLICY_FEE_STARTING:
    case AppConstants.PURCHASE_REFUND_BOOKING_POLICY_FEE_STARTING: {
      return {
        ...state,
        requestInProgress: true,
      };
    }
    case AppConstants.PURCHASE_CANCEL_CLEAR:
    case AppConstants.PURCHASE_REFUND_CLEAR:
    case AppConstants.PURCHASE_TRANSFER_CLEAR:
    case AppConstants.PURCHASE_CANCEL_FAILED:
    case AppConstants.PURCHASE_REFUND_FAILED:
    case AppConstants.PURCHASE_TRANSFER_FAILED:
    case AppConstants.PURCHASE_CHARGE_BOOKING_POLICY_FEE_FAILED:
    case AppConstants.PURCHASE_REFUND_BOOKING_POLICY_FEE_FAILED: {
      return {
        ...initialState,
        requestInProgress: false,
      };
    }
    default:
      return state;
  }
};

export default reducer;
